import { useCallback, useEffect } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSnapshot } from "valtio";
import { comparablesStore } from "src/js/stores/comparables";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import {
  PerUnitPriceModes,
  PriceModes,
  ZooplaPricePerUnitModes,
} from "react-migration/domains/comparables/typings/PriceModes";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { Dropdown } from "react-migration/components/Dropdown";
import { HomeTrackLogoLarge } from "react-migration/domains/comparables/img/HomeTrackLogoLarge";
import { setRHPdisplayBy } from "src/js/stores/comparables/actions";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";

const HometrackTag = () => {
  return <HomeTrackLogoLarge width={80} />;
};

export function ComparablesDisplayByDropdown() {
  const { t } = useTranslation();
  const { RHPdisplayBy, priceMode } = useSnapshot(comparablesStore);
  const hasZooplaAccess = hasFeature(Feature.zooplaFullAccess);

  const standardOptions = [
    {
      label: t(`comparables.filters.displayBy.${PerUnitPriceModes.PRICE}`),
      value: PerUnitPriceModes.PRICE,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${PerUnitPriceModes.PRICE}.description`)}
        </span>
      ),
    },
    {
      label: t(`comparables.filters.displayBy.${PerUnitPriceModes.EP}`),
      value: PerUnitPriceModes.EP,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${PerUnitPriceModes.EP}.description`)}
        </span>
      ),
    },
  ];

  const zooplaOptions = [
    ...standardOptions,
    {
      label: t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.VAL_LAST}`),
      value: ZooplaPricePerUnitModes.VAL_LAST,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.VAL_LAST}.description`)}
        </span>
      ),
      tailElement: <HometrackTag />,
    },
    {
      label: t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.VAL_ESTIMATE}`),
      value: ZooplaPricePerUnitModes.VAL_ESTIMATE,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.VAL_ESTIMATE}.description`)}
        </span>
      ),
      tailElement: <HometrackTag />,
    },
    {
      label: t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.RENT_ESTIMATE}`),
      value: ZooplaPricePerUnitModes.RENT_ESTIMATE,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.RENT_ESTIMATE}.description`)}
        </span>
      ),
      tailElement: <HometrackTag />,
    },
    {
      label: t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.RENT_ASKING}`),
      value: ZooplaPricePerUnitModes.RENT_ASKING,
      description: (
        <span className="text-xs text-neutral-500">
          {t(`comparables.filters.displayBy.${ZooplaPricePerUnitModes.RENT_ASKING}.description`)}
        </span>
      ),
      tailElement: <HometrackTag />,
    },
  ];

  const displayByOptions = hasZooplaAccess ? zooplaOptions : standardOptions;

  const handleDisplayByChange = useCallback(
    (value: PriceModes) => {
      setRHPdisplayBy(value);
      logEventV2({
        name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
        properties: { cardName: "comparables", filterName: "displayBy", filterState: value },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRHPdisplayBy]
  );

  useEffect(() => {
    if (!hasZooplaAccess && isZoopla(priceMode)) {
      setRHPdisplayBy(PerUnitPriceModes.PRICE);
    }
  }, [hasZooplaAccess, priceMode]);

  return (
    <div className="flex flex-row items-center justify-between w-full gap-2 p-2 pb-0 space-x-2 no-wrap">
      <label className="text-sm font-medium text-neutral-500">{t("comparables.filters.displayBy.label")}:</label>
      <div className="grow">
        <Dropdown
          options={displayByOptions}
          value={RHPdisplayBy}
          placeholder={t("comparables.filters.displayBy.placeholder")}
          handleValueChange={({ value }) => {
            handleDisplayByChange(value);
          }}
        />
      </div>
    </div>
  );
}
